import { RecordContextProvider, useTranslate } from "ra-core";
import { Link } from "react-router";
import { ReferenceField } from "@/components/admin/reference-field";
import { Card } from "@/components/ui/card";

import { RelativeDate } from "../misc/RelativeDate";
import { useProjects } from "../projects/useCurrentProject";
import type { Lead } from "../types";
import { leadRepresentation } from "./sources";

/**
 * Leads as a list, newest first: name and phone, then source, stage,
 * responsible (and project with «Все проекты»). Rows wrap on a phone.
 */
export const LeadRows = ({
  leads,
  showProject = false,
}: {
  leads: Lead[];
  showProject?: boolean;
}) => {
  const translate = useTranslate();
  const { projects, stages } = useProjects();
  if (!leads.length) {
    return (
      <p className="py-12 text-center text-sm text-muted-foreground">
        {translate("crm.leads.empty")}
      </p>
    );
  }
  return (
    <Card className="py-0 gap-0">
      <ul
        className="flex flex-col divide-y"
        aria-label={translate("crm.leads.list")}
      >
        {leads.map((lead) => {
          const stage = stages.find(
            (s) => String(s.id) === String(lead.stage_id),
          );
          const project = projects.find(
            (p) => String(p.id) === String(lead.project_id),
          );
          return (
            <li key={lead.id}>
              <RecordContextProvider value={lead}>
                <Link
                  to={`/leads/${lead.id}/show`}
                  className="flex flex-wrap items-baseline gap-x-4 gap-y-1 px-4 py-3 hover:bg-muted min-w-0"
                >
                  <span className="font-medium text-sm min-w-0 break-words flex-1 basis-48">
                    <span>{leadRepresentation(lead)}</span>
                    {lead.phone && lead.phone !== leadRepresentation(lead) ? (
                      <span className="ml-2 font-normal text-muted-foreground tabular-nums">
                        {lead.phone}
                      </span>
                    ) : null}
                  </span>
                  <span className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span>{translate(`crm.leads.sources.${lead.source}`)}</span>
                    {stage ? <span>{stage.name}</span> : null}
                    {showProject && project ? (
                      <span>{project.name}</span>
                    ) : null}
                    <span>
                      {lead.sales_id != null ? (
                        <ReferenceField
                          source="sales_id"
                          reference="sales"
                          link={false}
                        />
                      ) : (
                        translate("crm.leads.no_responsible")
                      )}
                    </span>
                    <span>
                      <RelativeDate date={lead.created_at} />
                    </span>
                  </span>
                </Link>
              </RecordContextProvider>
            </li>
          );
        })}
      </ul>
    </Card>
  );
};
